// InvoiceNamesConfig.tsx
import React, { useState, useEffect } from 'react';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { Upload } from 'lucide-react';

export const InvoiceConfig: React.FC = () => {
  const [activeSection, setActiveSection] = useState('general');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    // General Settings
    invoicePrefix: 'INV-',
    startingInvoiceNumber: '1001',
    defaultDueDays: '15 Days',
    defaultTerms: 'Late fees may apply after the due date. No refunds after filing. GST as applicable.',
    showTaskAssignee: false,
    
    // Company Details
    companyName: 'Goyal Traders',
    companyEmail: 'accounts@goyaltraders.com',
    companyPhone: '9876543210',
    companyAddress: 'B-17, Krishna Estate, Narol, Ahmedabad - 382405',
    
    // Bank Details
    bankName: 'HDFC Bank',
    branchName: 'Prahladnagar',
    accountNumber: '502000789456123',
    ifscCode: 'HDFC0001234',
    
    // Tax & Compliance
    gstNumber: '24ABCDE1234F125',
    
    // Branding
    companyLogo: null,
    authorizedStamp: null
  });

  const sections = [
    { id: 'general', label: 'General Settings' },
    { id: 'company', label: 'Company Details' },
    { id: 'bank', label: 'Bank Account Details' },
    { id: 'tax', label: 'Tax & Compliance' },
    { id: 'branding', label: 'Branding & Personalization' }
  ];

  // API Integration Functions
  const fetchInvoiceConfig = async () => {
    setIsLoading(true);
    try {
      // TODO: Replace with actual API call
      console.log('Fetching invoice configuration...');
      await new Promise(resolve => setTimeout(resolve, 1000));
    } catch (error) {
      console.error('Failed to fetch invoice configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveInvoiceConfig = async (data: typeof formData) => {
    setIsSaving(true);
    try {
      console.log('Saving invoice configuration:', data);
      await new Promise(resolve => setTimeout(resolve, 1000));
      return { success: true };
    } catch (error) {
      console.error('Failed to save invoice configuration:', error);
      return { success: false, error };
    } finally {
      setIsSaving(false);
    }
  };

  const resetToDefault = async () => {
    try {
      console.log('Resetting to default configuration...');
      await new Promise(resolve => setTimeout(resolve, 500));
      console.log('Configuration reset to defaults');
    } catch (error) {
      console.error('Failed to reset configuration:', error);
    }
  };

  const handleFileUpload = async (file: File, type: 'logo' | 'stamp') => {
    try {
      console.log(`Uploading ${type}:`, file.name);
      await new Promise(resolve => setTimeout(resolve, 1500));
      console.log(`${type} uploaded successfully`);
    } catch (error) {
      console.error(`Failed to upload ${type}:`, error);
    }
  };

  useEffect(() => {
    fetchInvoiceConfig();
  }, []);

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    const result = await saveInvoiceConfig(formData);
    if (result.success) {
      console.log('Settings saved successfully');
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset all settings to default?')) {
      await resetToDefault();
      await fetchInvoiceConfig();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>, type: 'logo' | 'stamp') => {
    const file = event.target.files?.[0];
    if (file) {
      handleFileUpload(file, type);
    }
  };

  const renderGeneralSettings = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">General Settings</h2>
          <p className="text-sm text-gray-600 mt-2">
            Define how your invoices are numbered, formatted, and when they're due.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Invoice Number Prefix</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add a custom prefix to your invoice numbers.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <div className="space-y-2">
            <Input
              value={formData.invoicePrefix}
              onChange={(e) => handleInputChange('invoicePrefix', e.target.value)}
              placeholder="INV-"
            />
            <div className="text-xs text-gray-500">
              Preview: {formData.invoicePrefix}2025-{formData.startingInvoiceNumber}
            </div>
          </div>
        </div>
      </div>

      {/* Subsequent Rows: Empty + Field + Input */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Starting Invoice Number</h3>
            <p className="text-sm text-gray-500 mt-1">
              Set the number your invoices should start from.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.startingInvoiceNumber}
            onChange={(e) => handleInputChange('startingInvoiceNumber', e.target.value)}
            placeholder="1001"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Default Due Days</h3>
            <p className="text-sm text-gray-500 mt-1">
              Set the default number of days from the invoice date when payment is due.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.defaultDueDays}
            onChange={(e) => handleInputChange('defaultDueDays', e.target.value)}
            placeholder="15 Days"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Default Terms & Conditions</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add any default terms that should appear at the bottom of every invoice — like late fees, refund policies, or compliance notes.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
            rows={4}
            value={formData.defaultTerms}
            onChange={(e) => handleInputChange('defaultTerms', e.target.value)}
            placeholder="Add any default terms..."
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Show Task Assignee on Invoices</h3>
            <p className="text-sm text-gray-500 mt-1">
              Display the name of the user who worked on the tasks included in the invoice.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <button
            onClick={() => handleInputChange('showTaskAssignee', !formData.showTaskAssignee)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formData.showTaskAssignee ? 'bg-blue-600' : 'bg-gray-200'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formData.showTaskAssignee ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>
    </div>
  );

  const renderCompanyDetails = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Company Details</h2>
          <p className="text-sm text-gray-600 mt-2">
            This information appears in the invoice header.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Company Name</h3>
            <p className="text-sm text-gray-500 mt-1">
              Enter the official name of your business. This will appear at the top of every invoice.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            placeholder="Goyal Traders"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Email Address</h3>
            <p className="text-sm text-gray-500 mt-1">
              Used for communication with clients and appears on the invoice footer.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.companyEmail}
            onChange={(e) => handleInputChange('companyEmail', e.target.value)}
            placeholder="accounts@goyaltraders.com"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Phone Number</h3>
            <p className="text-sm text-gray-500 mt-1">
              Optional. Displayed on the invoice for client contact or payment queries.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.companyPhone}
            onChange={(e) => handleInputChange('companyPhone', e.target.value)}
            placeholder="9876543210"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Address</h3>
            <p className="text-sm text-gray-500 mt-1">
              Provide your registered business address. Required for invoice compliance and client visibility.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <textarea
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none"
            rows={3}
            value={formData.companyAddress}
            onChange={(e) => handleInputChange('companyAddress', e.target.value)}
            placeholder="B-17, Krishna Estate, Narol, Ahmedabad - 382405"
          />
        </div>
      </div>
    </div>
  );

  const renderBankDetails = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Bank Account Details</h2>
          <p className="text-sm text-gray-600 mt-2">
            These details appear at the bottom of the invoice for payments.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Bank Name</h3>
            <p className="text-sm text-gray-500 mt-1">
              Enter the full name of the bank where you receive client payments.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.bankName}
            onChange={(e) => handleInputChange('bankName', e.target.value)}
            placeholder="HDFC Bank"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Branch Name</h3>
            <p className="text-sm text-gray-500 mt-1">
              Optional. Helps clients identify the correct bank branch when paying via NEFT/IMPS.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.branchName}
            onChange={(e) => handleInputChange('branchName', e.target.value)}
            placeholder="Prahladnagar"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Account Number</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add the account number where you want to receive invoice payments.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.accountNumber}
            onChange={(e) => handleInputChange('accountNumber', e.target.value)}
            placeholder="502000789456123"
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">IFSC Code</h3>
            <p className="text-sm text-gray-500 mt-1">
              Required for digital payments. Use the official 11-digit code for your bank branch.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.ifscCode}
            onChange={(e) => handleInputChange('ifscCode', e.target.value)}
            placeholder="HDFC0001234"
          />
        </div>
      </div>
    </div>
  );

  const renderTaxCompliance = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Tax & Compliance</h2>
          <p className="text-sm text-gray-600 mt-2">
            Set your business GST details for compliance-ready invoices.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">GST Number</h3>
            <p className="text-sm text-gray-500 mt-1">
              Enter your registered GSTIN (15 characters). This will appear on the invoice for tax compliance. Leave blank if not registered under GST.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <Input
            value={formData.gstNumber}
            onChange={(e) => handleInputChange('gstNumber', e.target.value)}
            placeholder="24ABCDE1234F1Z5"
          />
        </div>
      </div>
    </div>
  );

  const renderBranding = () => (
    <div className="space-y-8">
      {/* First Row: Section Heading + First Field */}
      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1">
          <h2 className="text-lg font-medium text-gray-900">Branding & Personalization</h2>
          <p className="text-sm text-gray-600 mt-2">
            Customize the appearance of your invoices with your logo and stamp.
          </p>
        </div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Company Logo</h3>
            <p className="text-sm text-gray-500 mt-1">
              Add your logo to show on every invoice PDF or printout. Recommended size: 300x100px.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Upload
            </p>
            <input
              type="file"
              id="logo-upload"
              className="hidden"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'logo')}
            />
            <Button 
              variant="secondary" 
              className="mt-2 text-xs"
              onClick={() => document.getElementById('logo-upload')?.click()}
            >
              Upload
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-8">
        <div className="col-span-1"></div>
        <div className="col-span-1">
          <div>
            <h3 className="text-sm font-medium text-gray-900">Signature or Authorized Stamp</h3>
            <p className="text-sm text-gray-500 mt-1">
              Upload a signature or official stamp to include at the bottom of all invoices.
            </p>
          </div>
        </div>
        <div className="col-span-1">
          <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center bg-gray-50">
            <Upload className="mx-auto h-8 w-8 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              Upload
            </p>
            <input
              type="file"
              id="stamp-upload"
              className="hidden"
              accept="image/*"
              onChange={(e) => handleFileChange(e, 'stamp')}
            />
            <Button 
              variant="secondary" 
              className="mt-2 text-xs"
              onClick={() => document.getElementById('stamp-upload')?.click()}
            >
              Upload
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-sm text-gray-600">Loading configuration...</p>
          </div>
        </div>
      );
    }

    switch (activeSection) {
      case 'general':
        return renderGeneralSettings();
      case 'company':
        return renderCompanyDetails();
      case 'bank':
        return renderBankDetails();
      case 'tax':
        return renderTaxCompliance();
      case 'branding':
        return renderBranding();
      default:
        return renderGeneralSettings();
    }
  };

  return (
    <div className="h-full flex bg-gray-50">
      {/* Left Sidebar Navigation */}
      <div className="w-64 bg-white border-r border-gray-200">
        <nav className="p-4 space-y-1">
          {sections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`w-full text-left px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeSection === section.id
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-6xl mx-auto px-8 py-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            {renderContent()}
            
            {/* Action Buttons */}
            {/* <div className="mt-12 pt-8 border-t border-gray-200 flex justify-end space-x-4">
              <Button 
                variant="secondary" 
                onClick={handleReset}
                disabled={isSaving}
              >
                Reset to Default
              </Button>
              <Button 
                variant="primary" 
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};